import React, { useState, useEffect } from 'react';
import type { ContentCategory } from '@/lib/api/types';
import { CloseIcon, UploadIcon, BinIcon } from '@/components/icons';
import { useAnnouncement, useCreateAnnouncement, useUpdateAnnouncement, useContentByCategory, useContentCategories } from '@/lib/api/hooks/announcement';
import { useFileUpload, useFileDelete } from '@/lib/api/hooks/file-upload';
import { showToast } from '@/lib/toast';
import { Loader } from '@/components/ui/Loader';
import { RichTextEditor } from '@/components/ui';

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  announcementId?: string;
  onSuccess?: () => void;
}

// Icon components for mode selection
const WriteIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HighlightIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 16H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ isOpen, onClose, announcementId, onSuccess }) => {
  const [mode, setMode] = useState<'write' | 'highlight'>('write');
  const [imageUrl, setImageUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contentCategory, setContentCategory] = useState('');
  const [highlight, setHighlight] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // For highlight mode
  const [selectedContentId, setSelectedContentId] = useState('');
  const [selectedContent, setSelectedContent] = useState<any>(null);

  const { data: announcementData, isLoading } = useAnnouncement(announcementId || '');
  const announcement = announcementData?.data;

  // Get content categories from API
  const { data: categoriesData, isLoading: isLoadingCategories } = useContentCategories();
  const categories = categoriesData?.data || [];

  // Helper function to format display names (replace underscores with spaces)
  const formatDisplayName = (displayName: string) => {
    return displayName.replace(/_/g, ' ');
  };

  const { data: contentData, isLoading: isLoadingContent } = useContentByCategory(
    contentCategory,
    mode === 'highlight' && !!contentCategory
  );

  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();
  const uploadMutation = useFileUpload();
  const deleteMutation = useFileDelete();

  // Extract contents array
  const contents = contentData?.success
    ? Array.isArray(contentData.data)
      ? contentData.data
      : (contentData.data as any)?.data || []
    : [];

  // Reset form when modal opens/closes or announcementId changes
  useEffect(() => {
    if (!isOpen) {
      // Reset everything when modal closes
      setMode('write');
      setImageUrl('');
      setTitle('');
      setDescription('');
      setContentCategory('');
      setHighlight(false);
      setSelectedContentId('');
      setSelectedContent(null);
      setUploadedFileName('');
      return;
    }

    // When modal opens, load data if editing
    if (announcement && announcementId) {
      setImageUrl(announcement.imageUrl || '');
      setTitle(announcement.title);
      setDescription(announcement.description);
      setContentCategory(announcement.contentCategory);
      setHighlight(announcement.highlight);
      setMode('write');
      setSelectedContentId('');
      setSelectedContent(null);
      setUploadedFileName('');
    } else if (!announcementId) {
      // Reset form for new announcement
      setMode('write');
      setImageUrl('');
      setTitle('');
      setDescription('');
      setContentCategory('');
      setHighlight(false);
      setSelectedContentId('');
      setSelectedContent(null);
      setUploadedFileName('');
    }
  }, [isOpen, announcement, announcementId]);

  const uploadImageToServer = async (file: File) => {
    setIsUploading(true);
    try {
      const uploadResult = await uploadMutation.mutateAsync({
        file,
        category: 'IMAGE'
      });

      if (!uploadResult.success || !uploadResult.data) {
        throw new Error('Image upload failed');
      }

      const imageUrl = uploadResult.data.fileUrl;

      if (!imageUrl) {
        throw new Error('Image URL not found in response');
      }

      setImageUrl(imageUrl);
      setUploadedFileName(file.name);
      showToast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Image upload failed:', error);
      showToast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast.error('Please upload an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        showToast.error('Image size must be less than 10MB');
        return;
      }
      uploadImageToServer(file);
    }
  };

  const handleDeleteImage = async () => {
    if (imageUrl && announcementId) {
      try {
        await deleteMutation.mutateAsync(imageUrl);
        showToast.success('Image deleted successfully');
      } catch (error) {
        console.error('Failed to delete image:', error);
        showToast.error('Failed to delete image');
      }
    }
    setImageUrl('');
    setUploadedFileName('');
  };

  const handleContentSelect = (content: any) => {
    setSelectedContent(content);
    setSelectedContentId(
      content.eventId || content.pressReleaseId || content.programmeId || content.strategicPillarId || ''
    );

    // Auto-populate fields from selected content
    let extractedImageUrl = '';
    let extractedTitle = '';
    let extractedDescription = '';

    switch (contentCategory) {
      case 'EVENT':
        extractedImageUrl = content.imageUrl || content.coverImageUrl || '';
        extractedTitle = content.eventTitle || content.title || '';
        extractedDescription = content.content || content.description || '';
        break;
      case 'PRESS_RELEASE':
        extractedImageUrl = content.coverImage || content.coverImageUrl || '';
        extractedTitle = content.title || '';
        extractedDescription = content.content || content.description || '';
        break;
      case 'PROGRAMME':
        extractedImageUrl = content.coverImageUrl || content.coverImage || '';
        extractedTitle = content.title || '';
        extractedDescription = content.content || content.description || '';
        break;
      case 'STRATEGIC_PILLAR':
        extractedImageUrl = content.coverImageUrl || content.coverImage || '';
        extractedTitle = content.title || '';
        extractedDescription = content.content || content.description || '';
        break;
    }

    setImageUrl(extractedImageUrl);
    setTitle(extractedTitle);
    setDescription(extractedDescription);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return '';
    }
  };

  const getContentPreview = () => {
    if (!selectedContent || !contentCategory) return null;

    let previewImage = '';
    let previewTitle = '';
    let previewLocation = '';
    let previewDate = '';
    let previewAuthor = '';

    switch (contentCategory) {
      case 'EVENT':
        previewImage = selectedContent.imageUrl || selectedContent.coverImageUrl || '';
        previewTitle = selectedContent.eventTitle || selectedContent.title || '';
        previewLocation = selectedContent.location || '';
        previewDate = formatDate(selectedContent.dateTime || selectedContent.datePublished);
        previewAuthor = selectedContent.createdByUsername || '';
        break;
      case 'PRESS_RELEASE':
        previewImage = selectedContent.coverImage || selectedContent.coverImageUrl || '';
        previewTitle = selectedContent.title || '';
        previewLocation = 'Global';
        previewDate = formatDate(selectedContent.datePublished);
        previewAuthor = selectedContent.createdByUsername || '';
        break;
      case 'PROGRAMME':
        previewImage = selectedContent.coverImageUrl || selectedContent.coverImage || '';
        previewTitle = selectedContent.title || '';
        previewLocation = '';
        previewDate = formatDate(selectedContent.datePublished || selectedContent.createdAt);
        previewAuthor = selectedContent.createdByUsername || '';
        break;
      case 'STRATEGIC_PILLAR':
        previewImage = selectedContent.coverImageUrl || selectedContent.coverImage || '';
        previewTitle = selectedContent.title || '';
        previewLocation = '';
        previewDate = formatDate(selectedContent.datePublished || selectedContent.createdAt);
        previewAuthor = selectedContent.createdByUsername || '';
        break;
    }

    return { previewImage, previewTitle, previewLocation, previewDate, previewAuthor };
  };

  const isFormValid = () => {
    // For write mode, need image, title, description, and contentCategory
    // For highlight mode, need contentCategory and selectedContent
    if (mode === 'write') {
      return imageUrl && title.trim() && description.trim() && contentCategory;
    } else {
      return contentCategory && selectedContent && imageUrl && title.trim() && description.trim();
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    // Validation
    if (!isFormValid()) {
      const missingFields = [];
      if (!imageUrl) missingFields.push('Image');
      if (!title.trim()) missingFields.push('Title');
      if (!description.trim()) missingFields.push('Description');
      if (!contentCategory) missingFields.push('Content Category');
      if (mode === 'highlight' && !selectedContent) missingFields.push('Content Selection');
      
      showToast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      setIsSaving(false);
      return;
    }

    try {
      const announcementType = selectedContentId ? 'LINKED' : 'MANUAL' as const;
      const payload = {
        imageUrl: imageUrl || '',
        title: title.trim(),
        description: description.trim(),
        contentCategory: contentCategory,
        highlight,
        announcementType,
        linkedContentId: selectedContentId ?? null
      } as const;

      if (announcementId) {
        await updateMutation.mutateAsync({
          announcementId,
          data: payload
        });
        showToast.success('Announcement updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        showToast.success('Announcement created successfully');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to save announcement');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const preview = getContentPreview();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-start z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Announcement</h2>
            <p className="text-sm text-gray-500 mt-1">Select section to set announcement</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSaving || isUploading}
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {isLoading && announcementId ? (
            <div className="text-center py-12">
              <Loader size="md" variant="wine" message="Loading announcement..." />
            </div>
          ) : (
            <>
              {/* Mode Selection - Only show for new announcements */}
              {!announcementId && (
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setMode('write');
                      setSelectedContent(null);
                      setSelectedContentId('');
                      setContentCategory('');
                      setImageUrl('');
                      setTitle('');
                      setDescription('');
                    }}
                    className={`flex-1 p-6 rounded-lg transition-colors ${
                      mode === 'write'
                        ? 'bg-[#00764B] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                        mode === 'write' ? 'bg-[#00A86B]' : 'bg-[#00A86B]'
                      }`}>
                        <WriteIcon className="w-8 h-8 text-white" />
                      </div>
                      <span className="font-semibold text-sm">Write New Announcement</span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setMode('highlight');
                      setImageUrl('');
                      setTitle('');
                      setDescription('');
                      setSelectedContent(null);
                      setSelectedContentId('');
                      setContentCategory('');
                    }}
                    className={`flex-1 p-6 rounded-lg transition-colors ${
                      mode === 'highlight'
                        ? 'bg-[#00764B] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                        mode === 'highlight' ? 'bg-[#00A86B]' : 'bg-[#00A86B]'
                      }`}>
                        <HighlightIcon className="w-8 h-8 text-white" />
                      </div>
                      <span className="font-semibold text-sm">Highlight Existing Content</span>
                    </div>
                  </button>
                </div>
              )}

              {/* Write Mode Content */}
              {mode === 'write' && (
                <>
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
                    {isUploading ? (
                      <div className="text-center border-2 border-dashed border-gray-300 rounded-lg p-10">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#00764B]"></div>
                        <p className="mt-4 font-semibold text-gray-700">Uploading image...</p>
                      </div>
                    ) : imageUrl ? (
                      <div className="relative">
                        <img src={imageUrl} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                        <div className="mt-2 flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <span className="text-sm text-gray-600 truncate">{uploadedFileName || 'Image uploaded'}</span>
                          <button
                            onClick={handleDeleteImage}
                            className="p-2 bg-red-50 rounded-lg transition-colors hover:bg-red-100"
                            type="button"
                          >
                            <BinIcon className="w-5 h-5 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer"
                        onClick={() => document.getElementById('image-upload')?.click()}
                      >
                        <UploadIcon className="w-12 h-12 mx-auto text-[#00764B] pointer-events-none" />
                        <p className="mt-2 font-semibold text-gray-700 pointer-events-none">Drag file to upload</p>
                        <p className="text-xs text-gray-500 mt-1 pointer-events-none">
                          Supported document file is PNG, & JPEG with max size of 10mb
                        </p>
                        <span className="mt-4 inline-block text-sm font-semibold text-[#00764B] hover:underline bg-[#E6F5F0] px-4 py-2 rounded-lg pointer-events-none">
                          Browse File
                        </span>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          disabled={isUploading}
                        />
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      id="title"
                      placeholder="Type here...."
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-[#00764B]"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <RichTextEditor
                      value={description}
                      onChange={setDescription}
                      placeholder="Type here...."
                      extendedToolbar={{ image: false }}
                      contentAreaClassName="min-h-[140px]"
                    />
                  </div>

                  {/* Content Category - Also needed in write mode */}
                  <div>
                    <label htmlFor="contentCategory" className="block text-sm font-medium text-gray-700 mb-2">
                      Content Category
                    </label>
                    <div className="relative">
                      <select
                        id="contentCategory"
                        value={contentCategory}
                        onChange={(e) => setContentCategory(e.target.value)}
                        className="select-wine w-full bg-white px-4 py-2 border border-gray-300 rounded-lg text-sm appearance-none pr-8"
                      >
                        <option value="">Select category</option>
                        {isLoadingCategories ? (
                          <option value="" disabled>Loading categories...</option>
                        ) : (
                          categories.map((cat: ContentCategory) => (
                            <option key={cat.value} value={cat.value}>
                              {formatDisplayName(cat.displayName)}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Highlight Mode Content */}
              {mode === 'highlight' && (
                <>
                  {/* Content Type */}
                  <div>
                    <label htmlFor="contentType" className="block text-sm font-medium text-gray-700 mb-2">
                      Content Type
                    </label>
                    <div className="relative">
                      <select
                        id="contentType"
                        value={contentCategory}
                        onChange={(e) => {
                          setContentCategory(e.target.value);
                          setSelectedContent(null);
                          setSelectedContentId('');
                          setImageUrl('');
                          setTitle('');
                          setDescription('');
                        }}
                        className="select-wine w-full bg-white px-4 py-2 border border-gray-300 rounded-lg text-sm appearance-none pr-8"
                      >
                        <option value="">Search or select option</option>
                        {isLoadingCategories ? (
                          <option value="" disabled>Loading categories...</option>
                        ) : (
                          categories.map((cat: ContentCategory) => (
                            <option key={cat.value} value={cat.value}>
                              {formatDisplayName(cat.displayName)}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Content List */}
                  {contentCategory && (
                    <div>
                      <label htmlFor="contentList" className="block text-sm font-medium text-gray-700 mb-2">
                        Content List
                      </label>
                      {isLoadingContent ? (
                        <div className="text-center py-4">
                          <Loader size="sm" variant="wine" />
                          <p className="mt-2 text-sm text-gray-500">Loading content...</p>
                        </div>
                      ) : (
                        <div className="relative">
                          <select
                            id="contentList"
                            value={selectedContentId}
                            onChange={(e) => {
                              const content = contents.find(
                                (c: any) =>
                                  (c.eventId || c.pressReleaseId || c.programmeId || c.strategicPillarId) === e.target.value
                              );
                              if (content) handleContentSelect(content);
                            }}
                            className="select-wine w-full bg-white px-4 py-2 border border-gray-300 rounded-lg text-sm appearance-none pr-8"
                          >
                            <option value="">Search or select option</option>
                            {contents.length > 0 ? (
                              contents.map((content: any) => {
                                const id =
                                  content.eventId ||
                                  content.pressReleaseId ||
                                  content.programmeId ||
                                  content.strategicPillarId;
                                const title =
                                  content.eventTitle ||
                                  content.title ||
                                  content.programmeTitle ||
                                  content.strategicPillarTitle ||
                                  'Untitled';
                                return (
                                  <option key={id} value={id}>
                                    {title}
                                  </option>
                                );
                              })
                            ) : (
                              <option value="" disabled>
                                No content available
                              </option>
                            )}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Content Preview Card */}
                  {preview && preview.previewTitle && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex gap-4">
                        {preview.previewImage && (
                          <img
                            src={preview.previewImage}
                            alt={preview.previewTitle}
                            className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1">{preview.previewTitle}</h4>
                          {preview.previewLocation && (
                            <p className="text-sm text-gray-600 mb-1">{preview.previewLocation}</p>
                          )}
                          {(preview.previewDate || preview.previewAuthor) && (
                            <p className="text-xs text-gray-500">
                              {preview.previewDate} {preview.previewAuthor && `| ${preview.previewAuthor}`}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Set as Highlight - Only for new announcements */}
              {!announcementId && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={highlight}
                      onChange={e => setHighlight(e.target.checked)}
                      className="checkbox-wine"
                    />
                    <div className="ml-3">
                      <p className="font-semibold text-gray-800">Set as Highlight</p>
                      <p className="text-xs text-gray-500">Display this announcement as highlighted</p>
                    </div>
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
          <button
            onClick={handleSubmit}
            disabled={!isFormValid() || isSaving || isUploading}
            className="w-full text-sm font-semibold text-white bg-[#00764B] rounded-lg px-6 py-3 hover:bg-[#005A3C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : announcementId ? 'Update Announcement' : 'Set Announcement'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;

